import { c as _c } from "react/compiler-runtime";
import { cn } from '../../utils/cn';
type ScrollFadeProps = {
  /**
   * Merged onto the gradient. Override `from-*` to match the surface it sits on
   * (`from-popover`, `from-background`, …), `h-*` to resize it, or set
   * `[--scroll-fade-range:64px]` to change how much scrolling it dissolves over.
   */
  className?: string;
};

/**
 * Gradient that fades the bottom edge of a scrollable container into the surface
 * below it, signalling there is more content to scroll to.
 *
 * Stays hidden while the container has nothing to scroll, and dissolves as the
 * scroll reaches the end. A CSS scroll timeline drives it, so there are no
 * listeners, observers, or state to keep in sync — it stays correct as content
 * grows and shrinks.
 *
 * Render it inside the element it should sit above (a sticky footer, a card
 * edge). That parent must be positioned — `sticky` already is — and must itself
 * live inside the scrolling element.
 *
 * The fade binds to the nearest ancestor **scroll container**, which in CSS
 * includes `overflow-hidden` and `overflow-clip` elements, not just scrollable
 * ones. A wrapper like `<div className="overflow-hidden">` between the fade and
 * the intended scroller will capture the binding and leave the fade permanently
 * hidden, since that wrapper never scrolls. Keep the fade inside the element
 * that actually scrolls.
 *
 * Needs Chrome 115+, Safari 26+, or Firefox 156+. Older engines drop
 * `animation-timeline` and hold the base opacity, so the fade never appears
 * rather than sticking on screen.
 *
 * @example
 * <div className="bg-card sticky bottom-0">
 *   <ScrollFade />
 *   <Button>Submit</Button>
 * </div>
 */
export function ScrollFade(t0) {
  const $ = _c(4);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("animate-scroll-fade from-card pointer-events-none absolute inset-x-0 bottom-full h-10 bg-linear-to-t to-transparent", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div aria-hidden={true} className={t1} />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}